import gql from 'graphql-tag';
import React, {useState} from 'react';

import isArchived from 'archive/isArchived';
import {POLYMER_SUBTYPE_TO_DISPLAY_NAME, PolymerSubtype} from 'constants/MolbioConstants';
import {NucleotideType, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AddPolymersWorklistItemsQuery} from 'molbio/bulk-assembly/add-polymers/__generated/AddPolymersWorklistItemsQuery';
import {AddPolymersModalSharedProps} from 'molbio/bulk-assembly/add-polymers/AddPolymersConstants';
import {updateFragmentsData} from 'molbio/bulk-assembly/add-polymers/AddPolymersUtil';
import {IMMERSIVE_MODAL_PROPS, NormalizedFragment} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {FragmentModalFragment} from 'molbio/bulk-assembly/polymer-map-modals/FragmentCardContents';
import FragmentsModalContent from 'molbio/bulk-assembly/polymer-map-modals/FragmentsModalContent';
import {fragmentDataToNormalizedFragment} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {listToReadable} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';
import {
  SelectItemsFromWorklistView,
  WORKLIST_ITEMS_CONTAINER_FRAGMENT,
} from 'worklists/SelectItemsFromWorklistView';
import {WorklistSummary} from 'worklists/WorklistConstants';

type BasicFolderItemNode = ExtractByTypename<
  ExtractByTypename<AddPolymersWorklistItemsQuery['worklist'], 'WorklistNode'>['items'][number],
  'BasicFolderItemNode'
>;

type WorklistItemNode = ExtractByTypename<
  AddPolymersWorklistItemsQuery['worklist'],
  'WorklistNode'
>['items'][number];

export const ADD_POLYMERS_WORKLIST_ITEMS_QUERY = gql`
  query AddPolymersWorklistItemsQuery($worklistId: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    worklist(id: $worklistId) {
      ... on WorklistNode {
        id
        items {
          ... on BatchNode {
            id
            name
          }
          ... on PlateNode {
            id
            name
          }
          ... on ContainerNode {
            ...WorklistItemContainerFragment
          }
          ... on FolderItemInterface {
            id
            name
          }
          ... on SequenceNode {
            id
            name
            archiveRecord {
              purpose
            }
            nucleotideType
            sequenceType
          }
          ... on AaSequenceNode {
            id
            name
            archiveRecord {
              purpose
            }
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${WORKLIST_ITEMS_CONTAINER_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

enum Steps {
  SELECT_WORKLIST = 'SELECT_WORKLIST',
  SELECT_FRAGMENTS = 'SELECT_FRAGMENTS',
}

/**
 * Helper for building the modal buttons that are available on the worklist selection step.
 * @param disableNext Whether the Next button should be disabled.
 */
function getSearchButtons(disableNext: boolean): Array<ModalButton> {
  return [
    {text: 'Cancel', cancel: true},
    {
      text: 'Next',
      style: ButtonStyle.SECONDARY,
      submit: true,
      disabled: disableNext,
      dataTestAttrs: {component: 'AddPolymersWorklistModal', key: ModalButtonKey.NEXT},
    },
  ];
}

/**
 * Helper for building the modal buttons that are available on the fragment selection step.
 * @param onClickBack Callback to execute when the Back button is clicked.
 */
function getFragmentSelectionButtons(onClickBack: () => void): Array<ModalButton> {
  return [
    {
      text: 'Back',
      style: ButtonStyle.TERTIARY_GRAY,
      onClick: onClickBack,
      disabled: false,
      dataTestAttrs: {component: 'AddPolymersSearchModal', key: ModalButtonKey.BACK},
    },
    {
      text: 'Add',
      style: ButtonStyle.SECONDARY,
      submit: true,
      disabled: false,
      dataTestAttrs: {component: 'AddPolymersWorklistModal', key: ModalButtonKey.ADD},
    },
  ];
}

function isWorklistItemNodeValidForAssembly(
  worklistItemNode: WorklistItemNode,
  polymerSubtypes: Array<PolymerSubtype>
): boolean {
  let polymerSubtype: PolymerSubtype;
  if (worklistItemNode.__typename === 'AaSequenceNode') {
    polymerSubtype = PolymerSubtype.aa_sequence;
  } else if (worklistItemNode.__typename === 'SequenceNode') {
    if (worklistItemNode.sequenceType === SequenceType.ds) {
      polymerSubtype = PolymerSubtype.dna_sequence;
    } else if (worklistItemNode.sequenceType === SequenceType.ss) {
      polymerSubtype = PolymerSubtype.rna_sequence;
    } else if (worklistItemNode.nucleotideType === NucleotideType.DNA) {
      polymerSubtype = PolymerSubtype.dna_oligo;
    } else {
      polymerSubtype = PolymerSubtype.rna_oligo;
    }
  } else {
    return false;
  }
  return polymerSubtypes.includes(polymerSubtype);
}

function getAllowedSubtypesErrorMessage(polymerSubtypes: Array<PolymerSubtype>): string {
  const polymerSubtypeDisplayNames = polymerSubtypes.map(
    (polymerSubtype) => `${POLYMER_SUBTYPE_TO_DISPLAY_NAME[polymerSubtype]}s`
  );
  return `Only ${listToReadable(polymerSubtypeDisplayNames, 'or')} may be used in this assembly.`;
}

/**
 * Modal component for adding new fragments to the bulk assembly from a worklist.
 *
 * Presents a two-step flow for selecting a worklist of sequences and then selecting fragments from those sequences.
 * TODO (BNCH-55735): Consolidate shared flow with AddPolymersSearchModal
 * @param assemblyId ID of the assembly. Used for analytics.
 * @param bulkAssemblyMethodData What kind of bulk assembly is being performed, and associated parameters.
 * @param canSpecifyEndpoints Whether the user can specify fragment endpoints.
 * @param canUpdateRestrictionEnzymes Whether the user can update restriction enzymes for the fragment.
 * @param onRequestSubmit Callback for adding the selected fragments to the assembly.
 * @param binColor Color of the bin the fragments will be added to.
 * @param enableSelectPreferredPrimers Whether to enable preferred primer selection during fragment selection.
 * @param binCid Bin id to add the new fragments to.
 * @param binIndex Index of the bin relative to other fragment bins.
 * @param polymerSubtypes Accepted polymer subtypes to search for.
 * @param onRequestClose Callback executed when the modal is closed or submitted.
 * @param constructTopology Topology of the assembly
 * @param numConstructFragments Number of fragment bins in the assembly
 * @param fragmentProductionMethod Fragment production method for the bin.
 * @constructor
 */
export default function AddPolymersWorklistModal({
  assemblyId,
  bulkAssemblyMethodData,
  canSpecifyEndpoints,
  canUpdateRestrictionEnzymes,
  onRequestSubmit,
  binColor,
  enableSelectPreferredPrimers,
  binCid,
  binIndex,
  constructFragmentIndex,
  polymerSubtypes,
  onRequestClose,
  constructTopology,
  numConstructFragments,
  fragmentProductionMethod,
}: AddPolymersModalSharedProps): JSX.Element {
  const [worklistSummary, setWorklistSummary] = useState<WorklistSummary | null>(null);
  const [selectedWorklistItems, setSelectedWorklistItems] = useState<Array<WorklistItemNode>>([]);
  const [currentStep, setStep] = useState<Steps>(Steps.SELECT_WORKLIST);
  const [fragmentsData, setFragmentsData] = useState<Array<FragmentModalFragment>>([]);

  const onClickNext = async (): Promise<void> => {
    await withLoadingScreen(
      updateFragmentsData(
        selectedWorklistItems.map((worklistItem) => (worklistItem as BasicFolderItemNode).id),
        fragmentsData,
        setFragmentsData,
        {
          polymerSubtypes,
          enableSelectPreferredPrimers,
          binCid,
          binColor,
          binIndex,
          constructFragmentIndex,
          canSpecifyEndpoints,
          canUpdateRestrictionEnzymes,
          constructTopology,
          numConstructFragments,
          bulkAssemblyMethodData,
          fragmentProductionMethod,
        }
      )
    );
    setStep(Steps.SELECT_FRAGMENTS);
  };

  const onClickBack = (): void => {
    setStep(Steps.SELECT_WORKLIST);
  };

  const buttons: Array<ModalButton> =
    currentStep === Steps.SELECT_WORKLIST
      ? getSearchButtons(selectedWorklistItems.length === 0)
      : getFragmentSelectionButtons(onClickBack);

  const modalStyleProps = currentStep === Steps.SELECT_WORKLIST ? {} : IMMERSIVE_MODAL_PROPS;

  return (
    <Modal
      {...modalStyleProps}
      title={currentStep === Steps.SELECT_WORKLIST ? 'Add from worklist' : 'Add fragment(s)'}
      buttons={buttons}
      onRequestClose={onRequestClose}
      onRequestSubmit={async () => {
        if (currentStep === Steps.SELECT_WORKLIST) {
          await onClickNext();
        } else {
          onRequestSubmit(
            fragmentsData.map((fragmentData): NormalizedFragment =>
              fragmentDataToNormalizedFragment(fragmentData)
            )
          );
        }
      }}
    >
      {currentStep === Steps.SELECT_WORKLIST ? (
        <SelectItemsFromWorklistView<AddPolymersWorklistItemsQuery>
          selectedWorklistItems={selectedWorklistItems}
          setSelectedWorklistItems={setSelectedWorklistItems}
          setWorklistSummary={setWorklistSummary}
          validateItem={(worklistItemNode) => {
            if (isInaccessible(worklistItemNode)) {
              return {isSelectorDisabled: true, tooltipText: "You don't have permissions to view this item."};
            } else if (isArchived(worklistItemNode)) {
              return {isSelectorDisabled: true, tooltipText: 'This item is archived.'};
            } else if (!isWorklistItemNodeValidForAssembly(worklistItemNode, polymerSubtypes)) {
              return {
                isSelectorDisabled: true,
                tooltipText: getAllowedSubtypesErrorMessage(polymerSubtypes),
              };
            }
            return {isSelectorDisabled: false};
          }}
          worklistSummary={worklistSummary}
          query={ADD_POLYMERS_WORKLIST_ITEMS_QUERY}
        />
      ) : (
        <FragmentsModalContent
          assemblyId={assemblyId}
          bulkAssemblyMethod={bulkAssemblyMethodData}
          canSpecifyEndpoints={canSpecifyEndpoints}
          fragmentsData={fragmentsData}
          setFragmentsData={setFragmentsData}
          isReadOnly={false}
          constructTopology={constructTopology}
          numConstructFragments={numConstructFragments}
        />
      )}
    </Modal>
  );
}
